const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// エラーレスポンスの型定義
type ApiErrorResponse = {
  error: string;
};

// カスタムエラークラス
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// エラーコードから日本語メッセージへのマッピング
const ERROR_MESSAGES: Record<string, string> = {
  QUALIFICATION_ALREADY_REGISTERED: "この資格は既に登録されています",
  USER_NOT_FOUND: "ユーザーが見つかりません",
  QUALIFICATION_TYPE_NOT_FOUND: "資格種別が見つかりません",
  DATABASE_OPERATION_FAILED: "データベースエラーが発生しました",
};

export const getErrorMessage = (error: unknown): string => {
  const defaultMessage = "登録に失敗しました";
  if (error instanceof ApiError) {
    return ERROR_MESSAGES[error.code] ?? defaultMessage;
  }
  return defaultMessage;
};

// エラーレスポンスをパースしてApiErrorをスロー
const throwApiError = async (res: Response, context: string): Promise<never> => {
  let errorCode = "UNKNOWN_ERROR";
  try {
    const errorBody: unknown = await res.json();
    if (errorBody && typeof (errorBody as ApiErrorResponse).error === "string") {
      errorCode = (errorBody as ApiErrorResponse).error;
    }
  } catch (e) {
    // JSONパースに失敗した場合はデフォルトのエラーコードを使用
    console.error("Failed to parse API error response as JSON:", e);
  }
  throw new ApiError(res.status, errorCode, `${context}: ${res.status} ${res.statusText}`);
};

export type QualificationType = {
  id: number;
  name: string;
  category: "deck" | "engine" | "common";
  defaultValidityMonths: number | null;
  parentQualificationId: number | null;
  createdAt: string;
};

// ユーザーの取得済み資格
export type UserQualification = {
  id: number;
  qualificationTypeId: number;
  qualificationTypeName: string;
  category: "deck" | "engine" | "common";
  parentQualificationId: number | null;
  acquiredAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  isExpired: boolean;
};

// 海技士の階層構造
export type MaritimeQualification = {
  id: number;
  name: string;
  category: "deck" | "engine" | "common";
  defaultValidityMonths: number | null;
  parentQualificationId: number | null;
  createdAt: string;
  writtenPass: QualificationType | null;
  subjects: QualificationType[];
};

// バッチ登録のレスポンス
export type BatchRegisterResponse = {
  results: { qualificationTypeId: number; success: boolean; error?: string }[];
  autoRegisteredWrittenPass: boolean;
};

export const fetchQualificationTypes = async (): Promise<QualificationType[]> => {
  const res = await fetch(`${API_BASE_URL}/api/qualification-types`);
  if (!res.ok) {
    await throwApiError(res, "Failed to fetch qualification types");
  }
  return res.json();
};

export const registerQualification = async (data: {
  userId: string;
  qualificationTypeId: number;
  acquiredAt: string;
}): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/qualifications`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    await throwApiError(res, "Failed to register qualification");
  }
};

// ユーザーの取得済み資格一覧を取得
export const fetchUserQualifications = async (userId: string): Promise<UserQualification[]> => {
  const res = await fetch(`${API_BASE_URL}/api/users/${userId}/qualifications`);
  if (!res.ok) {
    await throwApiError(res, "Failed to fetch user qualifications");
  }
  return res.json();
};

// 海技士の階層構造を取得
export const fetchMaritimeQualifications = async (): Promise<MaritimeQualification[]> => {
  const res = await fetch(`${API_BASE_URL}/api/maritime-qualifications`);
  if (!res.ok) {
    await throwApiError(res, "Failed to fetch maritime qualifications");
  }
  return res.json();
};

// 複数科目を一括登録
export const registerSubjectsBatch = async (data: {
  userId: string;
  qualificationTypeIds: number[];
  acquiredAt: string;
}): Promise<BatchRegisterResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/qualifications/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    await throwApiError(res, "Failed to register subjects");
  }
  return res.json();
};
